/**
 * The Radar Sites privacy boundary, expressed once.
 *
 * Two surfaces must stay out of third-party telemetry:
 *
 *   1. The control plane, exact `/sites` and everything under it. It carries
 *      workspace identity and, at `/sites/invitations/<token>`, a one-time
 *      bearer credential in the pathname.
 *   2. Customer tenant hosts under the configured hosting zone.
 *
 * Pure and dependency-free so it can be evaluated at Sentry client
 * initialization time — before React mounts — as well as inside components.
 * Host classification delegates to `classifyTenantHost` rather than
 * re-implementing suffix matching, so the client can never disagree with the
 * proxy or with the `next.config.ts` header rules about what a tenant host is.
 */

import { classifyTenantHost } from './hostname'

/**
 * The normalized hosting zone, mirrored into the client bundle by the `env`
 * block in `next.config.ts` from the SAME `normalizeHostingZone()` value the
 * tenant header/redirect pattern is built from. It is a public hostname, never
 * a secret, and there is no second operator-provided variable to keep in sync:
 * unset or misconfigured collapses to `null` here exactly as it does there.
 */
export const PUBLIC_SITES_HOSTING_ZONE: string | null =
  process.env.NEXT_PUBLIC_RADAR_SITES_HOSTING_ZONE || null

/** Exact `/sites` and everything under it — never `/sitesomething`. */
export function isSitesControlPlanePath(
  pathname: string | null | undefined
): boolean {
  if (typeof pathname !== 'string' || !pathname) return false
  return pathname === '/sites' || pathname.startsWith('/sites/')
}

/**
 * True when the CURRENT browser location is a Radar Sites surface: the control
 * plane, or any host at/beneath the hosting zone (tenant labels and reserved
 * ones alike — a reserved host is still Sites traffic, never PixelMojo).
 *
 * Returns false during SSR, which is correct for its only caller: the decision
 * is about instrumentation that exists only in the browser.
 */
export function isSitesBrowserSurface(): boolean {
  if (typeof window === 'undefined') return false
  if (isSitesControlPlanePath(window.location.pathname)) return true
  return (
    classifyTenantHost(window.location.hostname, PUBLIC_SITES_HOSTING_ZONE)
      .kind !== 'not-tenant'
  )
}
